import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import QRCode from 'qrcode';
import { GuestAttendanceApiService } from './guest-attendance-api.service';
import { GuestDay, GuestRegistrationView, GuestSettings } from './guest-attendance.types';

import { registrationFields, missingAnswer, questionError, receptionMessage, canChangeDay, japanTime } from './guest-registration-flow';

interface RegistrationPerson {
  id?: number;address?:string;email?:string;phone?:string;memberType?:string;answers:Record<string,any>;
  name: string;
  kana: string;
  organizationId: number | null;
  ageBand: string;
  gender: string;
  firstEvent: string;
  dayIds: number[];
  lockedDayIds: number[];
}

@Component({
  selector: 'app-guest-registration', standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './guest-registration.component.html',
  styleUrls: ['./guest-registration.component.css', './20260919_guest-registration-summary.css'],
})
export class GuestRegistrationComponent implements OnInit, OnDestroy {
  @Input() previewEvent?: GuestSettings;
  get previewOnly():boolean { return !!this.previewEvent; }
  token = ''; mode: 'register' | 'manage' = 'register'; step: 'intro' | 'form' | 'confirm' | 'complete' = 'intro';
  privacyAccepted = false; updateConfirm = false; now = Date.now(); private clock?: ReturnType<typeof setInterval>;
  private pendingUpdate?: {requestId:string;expectedRevision:number;people:Array<{id:number|undefined;dayIds:number[]}>};
  get updateLocked():boolean { return !!this.pendingUpdate; }
  canCancelPerson(person:RegistrationPerson):boolean { return !!this.registration?.days.some(day=>this.hasDay(person,day.id)&&this.dayEditable(person,day)); }
  fieldErrors: string[] = []; readonly fields = registrationFields;
  loading = true; saving = false; requiresCode = false; accessCode = ''; error = ''; message = '';
  event?: GuestSettings; registration?: GuestRegistrationView; people: RegistrationPerson[] = [];
  email = ''; phone = ''; result: any; qrDataUrl = ''; private requestId = crypto.randomUUID();
  surveyLinks:any[]=[];actualDates:Record<number,string>={};
  openSurvey(link:any){this.router.navigateByUrl(link.url,{state:{participantKey:link.participantKey,registrationToken:this.token}});}
  private receptionRequests=new Map<number,string>();
  readonly origin = location.origin;

  constructor(private route: ActivatedRoute, private api: GuestAttendanceApiService,private router:Router) {}

  ngOnInit(): void {
    if(this.previewEvent){this.event=structuredClone(this.previewEvent);this.loading=false;this.people=[this.emptyPerson()];return;}
    this.clock = setInterval(() => { this.now = Date.now(); }, 1000);
    this.token = String(this.route.snapshot.paramMap.get('token') || '');
    this.mode = this.route.snapshot.routeConfig?.path?.startsWith('guest/manage') ? 'manage' : 'register';
    this.mode === 'manage' ? this.loadRegistration() : this.loadEvent();
  }

  ngOnDestroy():void { if(this.clock) clearInterval(this.clock); }
  get isPeriod():boolean { return (this.event?.schedule || this.registration?.schedule)?.kind==='period'; }
  get hasDateChoice():boolean { return !this.isPeriod && (this.event?.days.length || 0)>1; }
  get dateHeading():string { return this.isPeriod?'利用期間':'参加日'; }
  get activeRegistration():boolean { return this.registration?.status==='active'; }
  get eventWritable():boolean { return !this.registration?.eventStatus || ['open','closed'].includes(this.registration.eventStatus); }
  get manageWritable():boolean { return this.activeRegistration && this.canWrite && this.eventWritable; }
  get managementNotice():string {
    if(!this.activeRegistration) return 'この申込みは取消済みです。受付QR・参加登録は利用できません。';
    if(!this.eventWritable) return this.registration?.eventStatus==='cancelled'?'イベントは中止になりました。':'現在、申込内容の変更・参加登録はできません。';
    return '';
  }
  get canApply():boolean { return (this.previewOnly || this.canWrite) && !!this.event?.days.some(d=>this.dayCanApply(d)); }
  dayCanApply(day:GuestDay):boolean { return this.previewOnly || day.canApply!==false&&day.remaining!==0&&(!day.registrationStart||this.now>=japanTime(day.registrationStart))&&(!day.registrationEnd||this.now<japanTime(day.registrationEnd)); }
  startApplication():void {
    if(!this.privacyAccepted || !this.canApply) return;
    this.step='form'; this.error=''; window.scrollTo({top:0,behavior:'smooth'});
  }
  fieldRequirement(index:number,key:string):string { return this.fieldPolicy(index,key)==='required'?'（必須）':'（任意）'; }
  formatTime(value?:string):string { return value?value.replace('T',' ').replaceAll('-','/'):'設定なし'; }
  get retentionText():string {
    if(this.event?.expiresAt) return `保存期限：${this.formatTime(this.event.expiresAt)}（日本時間）`;
    if(this.event?.retentionMonths==='contract') return '保存期間：主催組織のNEST契約期間中';
    if(this.event?.retentionMonths) return `保存期間：${this.isPeriod?'開催期間の終了日':'最初の開催日'}を基準に${this.event.retentionMonths}か月`;
    return '保存期間については主催者にお問い合わせください。';
  }
  fieldValue(person:RegistrationPerson,index:number,key:string):any {
    if(index===0 && key==='email') return this.email;
    if(index===0 && key==='phone') return this.phone;
    const field=this.fields.find(f=>f.key===key)!;
    return (person as any)[field.property];
  }
  answerLabel(value:any):string { return missingAnswer(value)?'未入力':Array.isArray(value)?value.join('、'):value==='na'?'該当なし':String(value); }
  fieldValueLabel(person:RegistrationPerson,index:number,key:string):string {
    const value=this.fieldValue(person,index,key);
    if(missingAnswer(value)) return '未入力';
    if(key==='organization') return this.event?.organizationOptions.find(o=>o.id===value)?.name||'未選択';
    return ({yes:'初めて',no:'参加経験あり',unknown:'わからない',member:'組合員',family:'家族',other:'その他'} as any)[value]||String(value);
  }
  dayEditable(person:RegistrationPerson,day:GuestDay):boolean { return this.manageWritable && canChangeDay(day,this.hasDay(person,day.id)||!!this.registration?.people.find(p=>p.id===person.id)?.days.some(d=>d.dayId===day.id&&d.bookingStatus==='booked'),this.isLocked(person,day.id),this.now); }
  get editableDays():boolean { return !!this.registration?.days.some(d=>this.people.some(p=>this.dayEditable(p,d))); }
  get changes():Array<{name:string;added:string[];removed:string[]}> {
    return this.people.map(person=>{
      const original=this.registration?.people.find(p=>p.id===person.id)?.days.filter(d=>d.bookingStatus==='booked').map(d=>d.dayId)||[];
      return {name:person.name,added:(this.registration?.days||[]).filter(d=>person.dayIds.includes(d.id)&&!original.includes(d.id)).map(d=>this.dayLabel(d)),removed:(this.registration?.days||[]).filter(d=>original.includes(d.id)&&!person.dayIds.includes(d.id)).map(d=>this.dayLabel(d))};
    }).filter(p=>p.added.length||p.removed.length);
  }
  get cancellingAll():boolean { return this.people.every(p=>!p.dayIds.length); }
  receptionHint(person:RegistrationPerson,day:GuestDay):string {
    if(this.isLocked(person,day.id)) return '参加登録済みです。';
    if(this.changes.length) return '先に参加日の変更を保存するか、画面を再読み込みしてください。';
    if(!this.manageWritable) return this.managementNotice||'現在は参加登録できません。';
    return receptionMessage(this.registration!.schedule,day,this.now);
  }
  canCheckin(person:RegistrationPerson,day:GuestDay):boolean { return !!this.registration?.schedule.selfCheckin && this.hasDay(person,day.id) && !this.receptionHint(person,day); }
  prepareUpdate():void {
    if(this.updateLocked || !this.manageWritable || !this.editableDays || !this.changes.length) return;
    this.updateConfirm=true; this.error='';
  }
  cancelPerson(person:RegistrationPerson):void {
    if(!this.registration || this.updateLocked || !this.canCancelPerson(person)) return;
    person.dayIds=person.dayIds.filter(id=>!this.dayEditable(person,this.registration!.days.find(d=>d.id===id)!));
    this.prepareUpdate();
  }
  emptyPerson(): RegistrationPerson {
    return {answers:{},name: '', kana: '', organizationId: null, ageBand: '', gender: '', firstEvent: '', dayIds: !this.hasDateChoice ? (this.event?.days||[]).filter(d=>this.previewOnly||d.canApply!==false&&d.remaining!==0).map(d=>d.id) : [], lockedDayIds: [] };
  }

  async loadEvent(): Promise<void> {
    this.loading = true; this.error = '';
    try {
      this.event = await this.api.event(this.token, this.accessCode);
      this.requiresCode = false;
      if (!this.people.length) this.people = [this.emptyPerson()];
    } catch (error: any) {
      const code = error?.error?.data?.code || error?.data?.code;
      this.requiresCode = code === 'access_code_invalid';
      this.error = this.requiresCode && this.accessCode ? '参加コードが一致しません。もう一度確認してください。' : this.requiresCode ? '' : this.errorText(error, '募集内容を表示できませんでした。');
    } finally { this.loading = false; }
  }

  async loadRegistration(): Promise<void> {
    this.loading = true; this.error = '';
    try {
      this.registration = await this.api.registration(this.token);
      try{this.surveyLinks=(await this.api.public<any>({action:'survey_links',token:this.token})).links||[];}catch{this.surveyLinks=[];}
      this.people = this.registration.people.map(person => ({
        answers:{},id: person.id, name: person.name, kana: person.kana, organizationId: person.organizationId,
        ageBand: person.ageBand || '', gender: person.gender || '', firstEvent: person.firstEvent || '',
        dayIds: person.days.filter(day => day.bookingStatus === 'booked').map(day => day.dayId),
        lockedDayIds: person.days.filter(day => day.actualStatus === 'attended').map(day => day.dayId),
      }));
      this.actualDates=Object.fromEntries(this.registration.people.map(person=>[person.id,person.days.find(day=>day.actualDate)?.actualDate||'']));
      this.qrDataUrl = this.activeRegistration && this.eventWritable && !this.registration.schedule.selfCheckin ? await QRCode.toDataURL(this.registration.qrPayload, { width: 280, margin: 2 }) : '';
      this.updateConfirm=false;
    } catch (error: any) { this.error = this.errorText(error, '申込内容を表示できませんでした。'); }
    finally { this.loading = false; }
  }

  fieldPolicy(index:number,key:string):string{const form=this.event?.registrationForm;if(index>0&&form?.companionMode==='name_only')return 'off';if(!form){if(key==='email')return this.event?.contactPolicy==='required'?'required':'optional';if(key==='phone')return 'optional';return (this.event?.collectedFields as any)?.[key]?(key==='organization'?'required':'optional'):'off';}return form[index>0&&form.companionMode==='individual'?'companion':'applicant'][key]||'off';}
  get maxPeople():number{return this.event?.companionPolicy==='individual'?1:this.event?.registrationForm?.maxGroupSize??20;}
  kanaPolicy(index:number):string{const form=this.event?.registrationForm;return !index||!form||form.companionMode==='same'?'required':form.companionMode==='name_only'?'off':form.companionKana||'required';}
  personQuestions(index:number):any[]{const form=this.event?.registrationForm;if(!form)return [];return form.questions.map(q=>({...q,policy:index&&form.companionMode==='name_only'?'off':index&&form.companionMode==='individual'?(form.companionQuestions?.[q.id]??q.policy):q.policy}));}
  extraToggle(person:RegistrationPerson,key:string,value:string,on:boolean){const values=person.answers[key]||[];person.answers[key]=on?[...new Set([...values,value])]:values.filter((v:string)=>v!==value);}
  addPerson(): void { if (this.people.length < this.maxPeople) this.people.push(this.emptyPerson()); }
  removePerson(index: number): void { if (index > 0 && this.people.length > 1) this.people.splice(index, 1); }
  isBooked(person:RegistrationPerson,dayId:number):boolean { return !!this.registration?.people.find(p=>p.id===person.id)?.days.some(d=>d.dayId===dayId&&d.bookingStatus==='booked'); }
  hasDay(person: RegistrationPerson, dayId: number): boolean { return person.dayIds.includes(dayId); }
  isLocked(person: RegistrationPerson, dayId: number): boolean { return person.lockedDayIds.includes(dayId); }
  toggleDay(person: RegistrationPerson, dayId: number, checked: boolean): void {
    if (this.isLocked(person, dayId)) return;
    person.dayIds = checked ? [...new Set([...person.dayIds, dayId])] : person.dayIds.filter(id => id !== dayId);
  }
  dayLabel(day: any): string { if((this.event?.schedule||this.registration?.schedule)?.kind==='period')return `${String(day.startAt).slice(0,10)}〜${String(day.endAt).slice(0,10)}`;return `${day.date.replaceAll('-', '/')} ${String(day.startAt).slice(11, 16)}〜${String(day.endAt).slice(11, 16)}`; }
  selectedDayLabels(person: RegistrationPerson): string[] {
    const days = this.mode === 'manage' ? this.registration?.days : this.event?.days;
    return (days || []).filter(day => person.dayIds.includes(day.id)).map(day => this.dayLabel(day));
  }
  get canWrite(): boolean { return (this.mode === 'manage' ? this.registration?.releaseControl?.canWrite : this.event?.releaseControl?.canWrite) !== false; }

  review(): void {
    this.error=''; this.fieldErrors=[];
    if(!this.canApply || !this.privacyAccepted){this.error='開催案内と申込受付状況を確認してください。';return;}
    if(this.people.length>this.maxPeople)this.fieldErrors.push(`1組の人数は申込者を含め${this.maxPeople}人までです。`);
    this.people.forEach((person,index)=>{
      const prefix=`${index+1}人目：`;
      if(!person.name.trim()) this.fieldErrors.push(prefix+'氏名を入力してください。');
      if(this.kanaPolicy(index)==='required'&&!person.kana.trim())this.fieldErrors.push(prefix+'ふりがなを入力してください。');
      if(!person.dayIds.length) this.fieldErrors.push(prefix+this.dateHeading+'を確認してください。');
      else if(person.dayIds.some(id=>!this.event?.days.some(d=>d.id===id&&this.dayCanApply(d)))) this.fieldErrors.push(prefix+'選択した参加日は申込みを受け付けていません。');
      for(const field of this.fields){
        if(this.fieldPolicy(index,field.key)==='off') continue;
        const value=this.fieldValue(person,index,field.key);
        if(this.fieldPolicy(index,field.key)==='required'&&missingAnswer(value)) this.fieldErrors.push(prefix+field.label+'を入力してください。');
        else if(field.key==='email'&&!missingAnswer(value)&&! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) this.fieldErrors.push(prefix+'メールアドレスを確認してください。');
      }
      for(const q of this.personQuestions(index)){const error=questionError(q,person.answers[q.id]);if(error)this.fieldErrors.push(prefix+q.title+'：'+error);}
    });
    if(this.fieldErrors.length){window.scrollTo({top:0,behavior:'smooth'});return;}
    this.step='confirm'; this.fieldErrors=[]; window.scrollTo({top:0,behavior:'smooth'});
  }

  async submit(): Promise<void> {
    if (this.previewOnly || this.saving || this.step!=='confirm' || !this.privacyAccepted || !this.canApply) return; this.saving = true; this.error = '';
    try {
      this.result = await this.api.register(this.token, {
        requestId: this.requestId, accessCode: this.accessCode, email: this.email, phone: this.phone,
        people: this.people.map(({ lockedDayIds, ...person }) => person),
      });
      this.qrDataUrl = !this.event?.schedule.selfCheckin ? await QRCode.toDataURL(this.result.qrPayload, { width: 280, margin: 2 }) : '';
      this.step = 'complete'; this.requestId = crypto.randomUUID();
    } catch (error: any) { this.error = this.errorText(error, '申込みを保存できませんでした。同じ内容で再試行できます。'); }
    finally { this.saving = false; window.scrollTo({ top: 0, behavior: 'smooth' }); }
  }

  async update(): Promise<void> {
    if (!this.registration || this.saving || !this.updateConfirm || (!this.pendingUpdate && (!this.manageWritable || !this.changes.length))) return;
    this.pendingUpdate ??= {requestId:this.requestId,expectedRevision:this.registration.revision,people:this.people.map(person=>({id:person.id,dayIds:[...person.dayIds]}))};
    this.saving = true; this.error = '';
    try {
      this.registration = await this.api.updateRegistration(this.token, this.pendingUpdate);
      this.pendingUpdate = undefined;
      this.message = this.registration.status==='cancelled'?'申込みを取り消しました。':'申込内容を変更しました。'; this.requestId = crypto.randomUUID();
      await this.loadRegistration();
    } catch (error: any) {
      const code=error?.error?.data?.code||error?.data?.code||error?.code;
      if(['revision_conflict','registration_locked','invalid_people','invalid_day','attended_day_locked','capacity_exceeded','past_change_deadline','outside_day_registration_period'].includes(code)){
        this.pendingUpdate=undefined;this.requestId=crypto.randomUUID();this.updateConfirm=false;await this.loadRegistration();
      }
      this.error = this.errorText(error, '結果を確認できませんでした。「同じ内容で結果を再確認」を押してください。');
    }
    finally { this.saving = false; }
  }

  async saveActualDate(person:RegistrationPerson,dayId:number){
    if(!this.registration||!person.id||this.saving)return;this.saving=true;this.error='';
    try{await this.api.public({action:'participation_date',token:this.token,personId:person.id,dayId,actualDate:this.actualDates[person.id]||'',expectedRevision:this.registration.revision,requestId:crypto.randomUUID()});this.message='参加日を保存しました。';await this.loadRegistration();}catch(e:any){this.error=this.errorText(e,'参加日を保存できませんでした。');}finally{this.saving=false;}
  }
  async selfCheckin(person:RegistrationPerson,dayId:number){
    const day=this.registration?.days.find(d=>d.id===dayId);
    if(!person.id||this.saving||!day||!this.canCheckin(person,day))return;this.saving=true;this.error='';
    const requestId=this.receptionRequests.get(person.id)||crypto.randomUUID();this.receptionRequests.set(person.id,requestId);
    try{await this.api.public({action:'self_checkin',token:this.token,personIds:[person.id],dayId,actualDate:this.actualDates[person.id]||'',requestId});this.receptionRequests.delete(person.id);this.message='参加を登録しました。';await this.loadRegistration();}
    catch(e:any){this.error=this.errorText(e,'参加を登録できませんでした。');}finally{this.saving=false;}
  }
  dayStatus(day:any){if(this.previewOnly)return '';return day.registrationState==='not_started'?'申込開始前':day.registrationState==='closed'?'申込締切':day.remaining===0?'満員':'';}
  copyEditUrl(): void { if (this.result?.editUrl) navigator.clipboard.writeText(this.origin + this.result.editUrl); }
  private errorText(error: any, fallback: string): string { return error?.error?.message || error?.message || fallback; }
}
